/**
 * Conflict-action copy — ALL user-visible strings for the conflict actions
 * live in THIS ONE MODULE, deliberately.
 *
 * v2 (2026-09-05, PRD `2026-09-05-conflict-actions-v2-and-thrive-status`):
 * ONE unified action list of FOUR mutually-exclusive actions per conflict
 * card. Sub-task 04's three CTAs and the pre-existing "Identity mismatch:
 * Skip / Update anyway" dropdown were two overlapping action surfaces; the
 * dropdown's "Update anyway" was always the missing fourth action, so it is
 * absorbed here and the dropdown is gone.
 *
 * fu5 sub-task 02 (2026-09-10) removed the neutral `doNothing` arm and moved
 * the surviving options onto full-sentence highlight rows — see
 * `CONFLICT_ACTION_SENTENCES` for the copy and the three arguments behind the
 * shape. Keeping every string here means a rename is still a one-file diff. Do
 * not inline any of these in components.
 *
 * All copy in English (app-content language rule).
 */

// `doNothing` ("Do nothing") IS GONE (fu5 sub-task 02, 2026-09-10), and it is
// not coming back under another name.
//
// It was the pre-selected default, and Jared asked three separate times what
// distinguished it from the dismissal. The answer he got — one is the empty
// state, the other is a decision — was correct and did not help. That is a
// finding about the product: if the person who commissioned the feature cannot
// hold the distinction after three explanations, a recruiter working a queue
// under time pressure will not hold it either.
//
//   "I think let's force them to tell us what to do. […] Let's force a
//    decision."  — Jared, 2026-09-08
//
// So a conflict row now starts UNDECIDED and Continue is blocked until every
// one has been answered. There is deliberately NO bulk "skip all remaining":
// clearing 50 rows in one click is the neutral default returning under a
// different label, and it removes the "somebody looked at each row" property
// more efficiently than the default it would replace. SETTLED, not open.
export const CONFLICT_ACTION_LABELS = {
  /** The persisted, undoable dismissal — excluded from every export path.
   *
   *  "Skip", not "Remove from list": nothing is removed — the row stays on the
   *  list and is left out of the export. The word landed on the BUTTON on
   *  2026-09-08 (as "Ignore") and on every other surface in fu5 sub-task 01;
   *  02 finished the sweep so the button, the pill, the tab, the table cell and
   *  the export panel all say one word for one state. The stored
   *  `review_status` is still `"ignored"` — that is the wire value, and it is
   *  the one thing this rename deliberately does not touch. */
  removeFromList: "Skip",
  /** The old "Different person": firm-wide registry pair — the contact
   *  classifies NEW from now on. */
  notSamePerson: "Not the same person",
  /** The old dropdown's "Update anyway", relocated: mark the row
   *  resolved-as-same-person; the update is WRITTEN when the flow continues
   *  ("Continue to Add to search") — batched, never an immediate per-row
   *  write. */
  updateAnyway: "Update anyway",
  /** "This is a different person — create them."
   *
   *  Offered where the identity question is open and "Not the same person"
   *  cannot be: that action needs a LinkedIn URL to key the firm-wide registry
   *  on, and a W3 with no URL has none. Without this the recruiter's only
   *  reachable moves were Update anyway (which writes onto the contact that
   *  ISN'T them) and Skip (which DROPS a real candidate) — neither of which
   *  says "create her". */
  addAsNew: "Add as new contact",
} as const;

export type ConflictAction = keyof typeof CONFLICT_ACTION_LABELS;

/** THE OPTION ROWS' COPY — one full sentence per action.
 *
 *  A separate map from `CONFLICT_ACTION_LABELS` because the two are read in
 *  different places and neither can do the other's job. The LABELS are the
 *  short names a BUTTON can carry (`CompanyChangeRow`'s Skip, an inline
 *  reference in prose); these are what a highlight-selected option ROW carries,
 *  where the affordance comes from the row and not from brevity.
 *
 *  WHY SENTENCES AND NOT BUTTONS (fu5 sub-task 02, three arguments, each of
 *  which has to be defeated on its own):
 *   1. a button carrying two sentences stops reading as a button — the
 *      affordance IS being short and imperative, and at sentence length the eye
 *      parses a paragraph and stops looking for the click;
 *   2. buttons do not communicate STATE, and state is the whole point: a
 *      recruiter sweeping 30 rows has to see at a glance which are decided, and
 *      that is also what makes "Continue is blocked, N rows remain" actionable;
 *   3. the hit-area idiom already exists in this repo (`CompanyChangeRow`'s
 *      full-width `<label>`), so reusing it means one idiom on the screen
 *      instead of two.
 *
 *  Each opens with its verb, so the row scans by its first word AND reads as
 *  English — the sentence is the label, not a caption under a name.
 *
 *  NOTE ON `removeFromList`: this is the PERSISTED dismissal
 *  (`PUT .../leave-out`, stored `review_status = "ignored"`, shown "Skipped",
 *  undoable, routed to its own tab). It is NOT the wire `W2Decision` value
 *  `"skip"`, which is a per-run deferral that re-raises on the next compare.
 *  Wiring this option to that value would silently downgrade a durable,
 *  undoable, tab-visible decision into a deferral. */
export const CONFLICT_ACTION_SENTENCES: Record<ConflictAction, string> = {
  removeFromList: "Skip this person for now. They stay on the list and are not added to Thrive.",
  notSamePerson:
    "These are not the same person. Remember that across the firm and stop matching them.",
  updateAnyway: "Same person. Update the matched Thrive contact with this row's details.",
  addAsNew: "Add this row to Thrive as a new contact and leave the matched record alone.",
};

/** Blocks Continue while any conflict row is unanswered, and NAMES THE COUNT.
 *
 *  The count is the actionable half. "Answer every row" tells a recruiter
 *  looking at a screen of highlighted rows nothing they can act on; "3 still
 *  need a decision" tells them how much is left, and the undecided rows are the
 *  ones with no fill — which is why the selected state had to be a fill rather
 *  than a pressed state for this sentence to be usable at all. */
export function undecidedBlockerLine(n: number): string {
  // NOT A BLOCKER ANY MORE — it names the CONSEQUENCE instead.
  //
  // fu5 gated Continue on this count, which collided head-on with a decision
  // that predates it and is stated in `export.spec.ts` F3: "Continue-to-Export
  // is ALWAYS available — Compare never disables it, never returns a 409/422
  // block" (v37 + 06/07, DECIDED). Two product rules, both real: Jared asked
  // to force a decision, and the standing rule is that Compare never
  // hard-blocks Export.
  //
  // They reconcile the way the operator put it (2026-09-10): an undecided row
  // is simply LEFT OUT of the export, and the modal says so. Forcing the
  // decision is what the cards do — no neutral default, a question with only
  // answers — not what the button does. A disabled Continue does not make a
  // recruiter decide; it makes them leave.
  return n === 1
    ? "1 contact still needs a decision. It will not be added to the search."
    : `${n} contacts still need a decision. They will not be added to the search.`;
}

/** Dimmed pill on a skipped (left-out) conflict card. Was "left out", then
 *  "ignored"; "skipped" since fu5 — the state reads the same word on every
 *  surface (chip, cell, tab, button), and a pill disagreeing with the tab it
 *  sits inside would read as two different states. */
export const LEFT_OUT_PILL = "skipped";

/** Helper line under a left-out card (operator-approved wording). */
export const LEAVE_OUT_HELPER = "won't be added to Thrive. You can undo this";

/** Undo CTA on a left-out card. */
export const UNDO_LEAVE_OUT_LABEL = "Undo";

/** Shown ONCE per browser session, the first time an Undo lands.
 *
 *  It answers a question the operator hit in the browser (2026-09-08): undo a
 *  dismissal and the contact does not reappear under `Needs review`. Nothing is
 *  lost — Compare EXCLUDES dismissed rows from its scope, so the run currently
 *  on screen never examined this one and has no verdict to show for it. The
 *  next run does.
 *
 *  ONCE, not on every click: it explains a mechanism, and a mechanism is
 *  learned the first time. Repeating it turns an explanation into noise, and
 *  noise is what gets a recruiter to stop reading the line that matters. */
export const UNDO_NEEDS_RECOMPARE_NOTE =
  "Restored. It will be examined again the next time you run Compare. The current results were produced without it.";

/** "skipped <local date and time>".
 *
 *  THE FUNCTION KEEPS ITS `Ignored` NAME, the string does not. `ignored` is the
 *  `review_status` WIRE VALUE this line dates — renaming the identifier would
 *  put the code one word away from the column it reads, for no reader's
 *  benefit. What the recruiter sees is "skipped", matching the button, the pill
 *  and the tab (fu5 sub-task 02).
 *
 *  The DATE AND THE TIME, on its own line (operator, 2026-09-08). Two dismissals
 *  minutes apart used to be indistinguishable, and "Sep 8" beside a word is the
 *  kind of date that reads as decoration rather than as a record. Older than
 *  this year drops the clock: the year is the useful precision by then.
 *
 *  Lives HERE, not in `W2ConflictCard`, since 2026-09-08: the Ignored tab now
 *  renders company changes as rows and identity conflicts as cards, and both
 *  have to date the dismissal identically. A private copy in each would be a
 *  twin — the exact defect class this epic spent its length removing. */
export function formatIgnoredDate(at: Date, now: Date): string {
  const opts: Intl.DateTimeFormatOptions =
    at.getFullYear() === now.getFullYear()
      ? { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }
      : { year: "numeric", month: "short", day: "numeric" };
  return `skipped ${at.toLocaleString(undefined, opts)}`;
}

// DIFFERENT_PERSON_DISABLED_TOOLTIP is GONE (2026-09-08). It explained why
// "Not the same person" was disabled on a row with no LinkedIn URL — honest,
// and useless: the recruiter who read it still had no way to say "this is
// someone else, create her". That state is now `addAsNew`, a working action
// instead of a disabled one with an explanation. The server's 409 on the same
// condition stays; the UI rule it backs is now "don't offer the action" rather
// than "offer it greyed out".

/** Shown in the warnings section when every conflict card was handled in THIS
 *  view but conflicts still exist. In v2 the ONLY way to empty the view while
 *  conflicts remain is "Not the same person" (do-nothing cards STAY visible),
 *  and those contacts do NOT come back — they classify NEW and are created on
 *  export. The v1 wording promised the opposite ("...will come back...") and
 *  was factually wrong in the only state that renders it (pr-review #838
 *  iter-1, A-F1 + C-1). */
export const CONFLICTS_HANDLED_FOR_NOW =
  "All conflicts here are resolved. Contacts marked as a different person will be created as new contacts when you export.";

/** "Sep 23, 2026" for an ISO instant; `null` when absent or unparseable. */
function noteDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** A combined list's Compare result is INHERITED from its source lists (no
 *  Compare ran on it, epic 2026-10-01-combine-stability-perf). Shown instead
 *  of "No conflicts", which would be false: conflict reviews (duplicate
 *  LinkedIn, identity mismatch, unverified name) do not carry over, and a
 *  contact the source Compare flagged as a conflict reads "not compared"
 *  here. `compared_from` / `compared_to` are null on a result written before
 *  they existed; the sentence then drops the date range. */
export function inheritedResultNote(
  inherited: { compared_from?: string | null; compared_to?: string | null } | null | undefined
): string {
  const from = noteDate(inherited?.compared_from);
  const to = noteDate(inherited?.compared_to);
  const when =
    from && to
      ? from === to
        ? `inherited from Compares run on ${from}`
        : `inherited from Compares run between ${from} and ${to}`
      : "inherited from the Compares run on the source lists";
  const lead = `These results are ${when}; no new Compare ran on this list.`;
  return `${lead} Contacts flagged as conflicts in the source lists read "not compared" here — run Compare to Thrive on this list to review them.`;
}

/** Inline error under the action control when a persisted action (remove
 *  from list / not the same person) fails — a silent no-op on a control that
 *  looks like a radio reads as a broken control (pr-review #838 iter-1, C-3). */
export const CONFLICT_ACTION_FAILED = "That didn't save. Check your connection and try again.";

// --- Sub-task 11 (compare UI states) — approved 2026-09-07 mocks ------------

/** Caption above each card's action control. */
export const CONFLICT_ACTION_CAPTION = "What do you want to do? (pick one)";

/** The two tabs. `Ignored` is the ONLY tab, in the sense that matters: it is
 *  the only thing behind one, and it is genuine archive.
 *
 *  TABS ARE WRONG FOR HIDING WORK, FINE FOR ARCHIVE (operator, 2026-09-08).
 *  The objection is the rule: someone resolves the visible tab, hits Continue,
 *  and never learns another tab held items — a risk that exists only for things
 *  still needing action. So everything pending lives in ONE scroll under
 *  `Needs review`, in four sections, and only the finished work is behind a
 *  second tab. */
export const NEEDS_REVIEW_TAB_LABEL = "Needs review";
export const IGNORED_TAB_LABEL = "Skipped";

/** Stats-block line counting the ignored (left-out) contacts — renders only
 *  when at least one exists. */
export function ignoredStatsLine(n: number): string {
  return `${n} skipped. They won't be added, and they are in the Skipped tab.`;
}

/** Title of the duplicate-LinkedIn warning banner at the top of the modal.
 *
 *  "on export" closes the sentence on the ACTION rather than on the place, so
 *  the banner, the table's row marker (which closes on "will duplicate on
 *  export") and the
 *  filter chip ("Will duplicate") all name the same not-yet-made write. The
 *  near-opposite fact — Thrive already holds several contacts on one URL — is
 *  the W1 finding, and it now says "already duplicated in Thrive" everywhere.
 *  Ending this title "…in Thrive" put the two one preposition apart, and the
 *  operator read one as the other while hand-testing (2026-09-08). */
export function dupWarningTitle(n: number): string {
  return n === 1
    ? "1 contact will duplicate a LinkedIn URL on export"
    : `${n} contacts will duplicate a LinkedIn URL on export`;
}

/** Per-contact line inside the banner. With a candidate id the line names the
 *  Thrive contact whose URL is shared; without one (the excluded pair's id is
 *  not on this card) it states the registry fact alone. Both are FUTURE tense,
 *  same reason as the title. */
export function dupWarningLine(name: string, thriveId: string | null | undefined): string {
  return thriveId
    ? `${name} will share the URL of Thrive #${thriveId}.`
    : `${name} will duplicate a LinkedIn URL already ruled out for another Thrive contact.`;
}

// w1AlreadyDuplicatedLine is GONE (2026-09-08). It built the W1 card's opening
// sentence by interpolating the duplicates' NAMES into prose ("shared by Maria
// Johnson (#1042) and Maria Johnson (#3318)"). The card now renders those names
// as rows on its right-hand side, where two faces beside two names are the
// evidence the sentence could only describe. `W1_VERDICT` replaces it and says
// one thing.

// --- Third round (2026-09-08): the four PENDING sections, in one scroll -----
//
// Ordered by how cheaply each closes, which is the same principle the previous
// round applied to a single queue — the sections make the ranking readable
// instead of inferable, and their COUNTS stay visible even when a section is
// paged or filtered, so no amount of work is ever silently out of view.

/** 1. Same name, different employer. The NORMAL case in a recruiting database,
 *  and it lands in the conflict machinery only because the identity rule keys
 *  on name+company. Compact rows, pre-confirmed, one `Confirm all`.
 *
 *  "Update for current role" since 2026-09-08, Jared's own wording and his own
 *  correction — he proposed "update to current company" mid-sentence and then
 *  settled it: *"It could even say update for current role. That's probably the
 *  best way to say it."*
 *
 *  What the old name got wrong was the VERB, not the scope. *"That's not
 *  company changes… we don't want to change the company, that remains their
 *  history. We want to update it for the new role they have."* Thrive keeps the
 *  old employer as history and gains the new position; nothing about the
 *  company is edited. A section headed "Company change" promised a destructive
 *  edit the feature does not perform, on the record a recruiter is most
 *  protective of.
 *
 *  The identifier keeps `COMPANY_CHANGE` — the internal name follows the
 *  `bct` / "Company Map" precedent, where only the recruiter-visible words
 *  moved. */
export const COMPANY_CHANGE_SECTION = "Update for current role";
/** The section's lead, and — since 2026-09-08 — its ACTION CAPTION too.
 *
 *  Every other section in the pending tab states the question above its
 *  controls (`CONFLICT_ACTION_CAPTION`, "What do you want to do? (pick one)"),
 *  and this one stated only what the rows ARE. That left the Ignore button
 *  orphaned: a control with nothing above it saying which question it answers.
 *  Operator, 2026-09-08 — "que el layout de ignore sea análogo a las secciones
 *  que admiten ignore".
 *
 *  It names all THREE answers because the row really does offer three, split
 *  across two controls: ticked updates, unticked defers, Skip drops. A reader
 *  who only sees the checkbox cannot discover the third from the tick alone.
 *
 *  THE PRE-TICKED BOX SURVIVES fu5's "no defaults" sweep, deliberately. These
 *  rows are a job move, not a conflict — they reach the conflict machinery only
 *  because the identity rule keys on name + company — and Jared reviewed the
 *  section live on 2026-09-08 without objecting to the tick. The modal already
 *  materialises it into a real `update_anyway` on Continue, so what is on
 *  screen is what is sent. Do not "fix" it for consistency with the conflict
 *  cards: it is an exception with a reason on the record. */
export const COMPANY_CHANGE_LEAD =
  "Same person, new employer. Ticked rows update the company in Thrive; " +
  "untick to leave one alone, or Skip to drop it from this export.";
export function confirmAllLabel(n: number): string {
  return `Confirm all ${n}`;
}
/** Per-row employer transition. Either side can be blank — a Thrive contact
 *  with no employer on file is common, and "— → Globex" still reads. */
export function companyTransition(from?: string | null, to?: string | null): string {
  return `${from?.trim() || "no employer on file"} → ${to?.trim() || "no employer on file"}`;
}
/** Behind the section's ⓘ. Says what the pre-confirmation MEANS, because a
 *  checked box the recruiter did not tick is a promise the modal has to keep. */
export const COMPANY_CHANGE_DETAIL =
  "These rows matched a Thrive contact by LinkedIn URL with the same name and a " +
  "different employer, which is a job move rather than a conflict. They are " +
  "confirmed by default and will update the matched contact when you continue. " +
  "Uncheck any row you are not sure about and it will be left alone.";

/** 2. The real identity conflicts (differing name) plus every W3. */
export const NEEDS_DECISION_SECTION = "Needs your decision";

// IGNORED_EARLIER_SECTION / _LEAD are GONE (2026-09-08). They headed a third
// section for rows dismissed before this compare run — a distinction the
// RECRUITER does not have. They ignored a contact; whether that predates the
// last run is a fact about our data retention, and putting it on screen showed
// them our limitation dressed as information. The rows now sit in
// `Needs your decision` and simply carry less per row.

/** Shown when the import holds MORE dismissals than the one page the modal
 *  fetches (the contacts endpoint caps `page_size` at 100).
 *
 *  It exists so the list under-reports OUT LOUD. The section's count is the
 *  server's own total, so the number stays right either way; without this line
 *  the ROWS would silently disagree with it, and a list showing 100 under a
 *  heading that says 140 is the kind of quiet mismatch this epic keeps
 *  finding. */
export function priorTruncatedNote(n: number): string {
  return n === 1
    ? "1 more was skipped earlier and is not listed here."
    : `${n} more were skipped earlier and are not listed here.`;
}
/** 3. Decided, but a task remains. NOT archive — see the ⓘ below.
 *
 *  "Same LinkedIn URL on two people", not the operator's literal "LinkedIn URL
 *  to fix in one side" — same meaning, and it reads as English rather than as a
 *  translation. The two things it has to carry are both there: the URL is what
 *  is wrong, and it is on TWO records, so exactly one of them has to change.
 *  What it drops is "to fix", which the section's position already says (it
 *  sits in the pending scroll, not behind the Ignored tab) and which the ⓘ then
 *  spells out step by step.
 *
 *  It also stopped naming the DECISION. "Different people" described what the
 *  recruiter already settled; the open task is the collision that decision left
 *  behind, and a section header in a pending list should name the work. */
export const DIFFERENT_PEOPLE_SECTION = "Same LinkedIn URL on two people";

/** 4. Nothing in this product can fix these. */
export const CANT_RESOLVE_SECTION = "Can't be resolved here";

/** The filter chips over the pending scroll. They NARROW a long list; nothing
 *  is hidden by default, which is the whole difference from the tabs they
 *  replaced. */
export const PENDING_FILTER_ALL = "All";

/** Empty states for the tabs and sections. */
export const NEEDS_REVIEW_TAB_EMPTY = "Nothing needs your review.";
export const IGNORED_TAB_EMPTY = "No skipped contacts.";

// --- Second design pass (2026-09-08 operator hand-testing) -------------------
//
// TEXT IS THE LAST RESORT. Every card gets ONE verdict line; anything longer
// lives behind an info icon. The rule came from the operator watching the
// approved mocks — "por principio que no haya mucho texto, en todo caso info
// icons con detalles mas explicativos" — and it is a rule about SCANNING: a
// recruiter working through ten cards reads ten verdict lines, not ten
// paragraphs. Everything under `*_DETAIL` below is the paragraph, moved.

// DIFFERENT_PEOPLE_TAB_LABEL / _TAB_EMPTY are GONE (2026-09-08, third round).
// Those rows had a tab of their own for about one revision. They are not
// archive — marking "not the same person" answers one question and opens
// another — so they moved into the pending scroll as a SECTION, and
// `DIFFERENT_PEOPLE_SECTION` names them there. A section that renders only
// when non-empty needs no empty state.

/** Header of a card's right-hand column. States the SIDE and the COUNT, so
 *  "which side am I looking at" is never inferred from position alone. */
export function inThriveHeader(n: number): string {
  return `In Thrive (${n})`;
}

/** Header of a card's left-hand column — the constant, your own row. */
export const FROM_YOUR_LIST_HEADER = "From your list";

// CANT_RESOLVE_HERE_DIVIDER is GONE (2026-09-08, third round). It marked where
// the resolvable work ended INSIDE one merged queue. The queue is now four
// named sections, so the boundary is a section header rather than a line drawn
// between two kinds of card — `CANT_RESOLVE_SECTION` carries the same words to
// the same purpose, and carries a count with them.

// --- W1: already duplicated in Thrive (NOT resolvable here) -----------------

/** The W1 verdict line. */
export const W1_VERDICT = "Already duplicated in Thrive. Fix it there.";

/** Behind the W1 card's info icon.
 *
 *  Names BOTH readings instead of choosing, because we cannot tell them apart
 *  programmatically and the recruiter looking at the two photos can. It also
 *  states the honest gap: of the two real exits, only one has product support. */
export const W1_DETAIL =
  "A LinkedIn URL belongs to one person, so either these are different people " +
  "and one URL is wrong, or it is one person duplicated in Thrive and the " +
  "records should be merged. The photos and names on the right are the " +
  "evidence. We cannot tell which case this is. Both are fixed in Thrive, and " +
  "fixing the source list instead is not something this product can do yet.";

/** The W1 footer when we know the URL is shared but not by WHOM — the
 *  `detect_w1` branch with no resolvable `duplicate_thrive_ids`.
 *
 *  A card with no footer reads as broken, which is the one thing the
 *  not-resolvable card kind exists to avoid. With no ids there is no door to
 *  open, so it names the place instead of offering a link it cannot build. */
export const W1_NO_LINK_FALLBACK = "Search this LinkedIn URL in Thrive to find the duplicates.";

/** The W1 footer when the duplicates ARE linked from the rows above.
 *
 *  It replaced `openInThriveLabel` + a link per duplicate, which duplicated the
 *  deep links the right-hand rows already carry (operator, 2026-09-08). The
 *  footer's job on a not-resolvable card is to name WHERE the fix lives — the
 *  rows are the route, this is the destination. */
export const W1_FIX_IN_THRIVE = "Open the records above and merge or correct them in Thrive.";

// --- W2 / W3 verdicts -------------------------------------------------------

/** The W2 verdict line.
 *
 *  "LinkedIn URL", not "URL" (operator, 2026-09-08). This is a VERDICT — the
 *  one line a recruiter reads while scanning, before any detail is open — so it
 *  carries no context to disambiguate from it. The bare noun could be the
 *  Thrive record's URL, the import row's, or a company URL; naming it removes a
 *  question the reader should not have to ask. The `W2_DETAIL` below already
 *  said "The LinkedIn URL", so the two now agree rather than the shorter one
 *  being vaguer. */
export const W2_VERDICT = "Thrive's record for this LinkedIn URL looks like someone else.";
export const W2_DETAIL =
  "The LinkedIn URL matches this Thrive contact exactly, but the name or the " +
  "employer does not. That is either a different person on a wrong URL, or the " +
  "same person who changed jobs. Compare the two sides and decide. A row you " +
  "leave unanswered is not added to the search.";

export const W3_VERDICT = "Name matches, and nothing can confirm it.";
export const W3_DETAIL =
  "One of the two sides has no LinkedIn URL, so the name match cannot be " +
  "confirmed or ruled out. The photos are the tiebreaker. If they are not the " +
  "same person, add this row as a new contact. Thrive's record is left alone.";

// --- Card B: marked as a different person (resolvable here: Undo) -----------

/** The Different people card's verdict — confirms the decision AND names what
 *  it will cost on export.
 *
 *  It used to stop at "That decision stands." — a GREEN line, in the resolved
 *  tone, that told the recruiter everything was settled while omitting the one
 *  thing that is not: exporting this row CREATES a second Thrive contact on a
 *  LinkedIn URL another contact already holds. The decision standing and the
 *  duplicate being made are both true, and the card was stating only the
 *  reassuring half (operator, 2026-09-08).
 *
 *  Green is still right — the recruiter answered the question that was asked,
 *  and nothing here is a mistake to fix. What was wrong was a verdict that
 *  read as "nothing further happens". The consequence rides the same sentence
 *  rather than the ⓘ, because a note behind an icon is exactly where the
 *  duplicate would keep being missed. */
export const DIFFERENT_PERSON_VERDICT =
  "You marked these as different people. That decision stands. Exporting will " +
  "create a second Thrive contact on this LinkedIn URL.";

/** Behind that card's ⓘ — the deliverable the operator named specifically.
 *
 *  It answers "WHAT DO I DO WITH THIS CONTACT", which is why it is not a
 *  restatement of the warning: the recruiter already knows something is off,
 *  and the row is in a PENDING section rather than an archived one precisely
 *  because a human task remains. Marking "not the same person" answers one
 *  question and opens another, and nothing else in the product tracks it.
 *
 *  The closing line is the payoff of the compare-time registry retirement: told
 *  the flag disappears by itself, nobody goes hunting for a "mark as done"
 *  button — and there must not be one, because the row's whole meaning is that
 *  two records still collide. */
export const DIFFERENT_PERSON_DETAIL = {
  title: "Two records, one LinkedIn URL",
  body:
    "You marked these as different people, and that stands. But a LinkedIn URL " +
    "belongs to exactly one person, so one of these two records has the wrong " +
    "URL. Until it is corrected, this URL will keep matching the wrong contact " +
    "on every future compare.",
  fixLabel: "To fix it:",
  fix:
    "open both records, decide which URL is wrong, and correct it in Thrive. If " +
    "the wrong one is in your source list, it has to be fixed before the next " +
    "import. There is no way to edit an imported row today.",
  selfClearing: "This row will clear itself once the URLs no longer collide.",
} as const;

/** Undo CTA on a Different people card. */
/** The second line under each re-check option: WHAT WILL BE CHECKED.
 *
 *  Split from the action because they are different statements. The label is
 *  the recruiter's ("I made changes"); this is ours, and it is the half that
 *  was never said anywhere: the W1 threshold is ONE surviving record, not zero,
 *  so a recruiter who took a pile of four down to two had no way to know that
 *  was not enough. The different-person card's threshold is zero, because its
 *  row is about to be CREATED alongside whatever remains. */
export const RECHECK_CONDITION_THRIVE_DUPLICATES =
  "We will check that only one record in Thrive has this URL.";
export const RECHECK_CONDITION_DIFFERENT_PERSON =
  "We will check that no one else in Thrive has this URL.";

export const UNDO_DIFFERENT_PERSON_LABEL = "Undo this decision";

/** Attribution line under a Different people card's right-hand entry. */
export function differentPersonAttribution(by: string, when: string): string {
  return `ruled out by ${by} · ${when}`;
}

/** Inline error when the Undo call fails. Distinct from
 *  `CONFLICT_ACTION_FAILED` only in that the reader is undoing, not deciding. */
export const UNDO_DIFFERENT_PERSON_FAILED =
  "That didn't undo. Check your connection and try again.";

// --- 'Add as new contact' ---------------------------------------------------

/** Behind the info icon beside `Add as new contact`.
 *
 *  States the durability plainly rather than letting the recruiter assume it.
 *  The decision rides the same request-time map as `Update anyway` — it is not
 *  persisted, and it CANNOT be: with no LinkedIn URL there is no registry
 *  identity to remember it under, which is the same reason 'Not the same
 *  person' is unavailable on the cards that offer this one. */
// --- The export panel's own per-row dropdown --------------------------------

/** The `skip` arm of `ExportToThrivePanel`'s "Identity mismatch:" `<select>`.
 *
 *  A SEPARATE STRING FROM EVERY `Skip` ABOVE, and the separation is the point.
 *  That dropdown writes the WIRE `W2Decision` value `"skip"`, which is a
 *  per-run deferral: the row is left out of THIS export and the conflict raises
 *  again on the next compare. The modal's `Skip` is the persisted, undoable
 *  dismissal. Two different things, and until fu5 sub-task 02 this option read
 *  "Do nothing" — the label whose whole problem was that it named neither.
 *
 *  It says "for this export" out loud so the two are told apart on the one
 *  screen a recruiter could meet both on. This is the only surviving caller of
 *  the wire `skip` value: after 02 the conflict cards cannot reach it. */
export const EXPORT_W2_DEFER_LABEL = "Leave out of this export";

export const ADD_AS_NEW_DETAIL =
  "Creates this row in Thrive as a new contact and leaves the matched record " +
  "untouched. It applies to this export only. Reloading or re-running Compare " +
  "raises the row again, because there is no LinkedIn URL to remember the " +
  "decision under.";

// --- fu5 sub-task 02, Part B: the duplicate-LinkedIn card's two options -----
//
// EXACTLY TWO, and the door on a third is closed (Jared, 2026-09-08):
//
//   "There's no other option. We shouldn't add them if they don't clean it up.
//    And if they clean it up, then we can add them for them. Those are really
//    the only two options."
//
// `UNDO_DIFFERENT_PERSON_LABEL` is NOT a third option in that sense — it
// reverses the EARLIER 'Not the same person' call rather than answering *what
// do we do now*, and removing it would strand a firm-wide permanent decision
// with no reversal surface anywhere in the product. It stays where it is, in
// the card's footer, visually subordinate to these two rows.

/** The duplicate-LinkedIn card's decision vocabulary.
 *
 *  A SEPARATE union from `ConflictAction`, deliberately. The two cards answer
 *  different questions — a conflict card asks "is this the same person?", this
 *  one asks "did you clean up the collision that answer left behind?" — and
 *  merging them would offer `updateAnyway` / `notSamePerson` on a card where
 *  the identity question is already settled. */
export type DuplicateLinkedInAction = "fixedInThrive" | "skipForNow";

/** The two option rows' copy.
 *
 *  `skipForNow` IS THE SAME PERSISTED ACTION as `CONFLICT_ACTION_LABELS.
 *  removeFromList` — `PUT .../leave-out`, stored `review_status = "ignored"`,
 *  shown "Skipped", undoable, routed to the Skipped tab. It is NOT the wire
 *  `W2Decision` value `"skip"`, which is a per-run deferral that re-raises on
 *  the next compare; wiring it there would silently downgrade a durable,
 *  undoable, tab-visible decision into a deferral.
 *
 *  The sentence is stated here rather than reused from
 *  `CONFLICT_ACTION_SENTENCES.removeFromList` because this card's pair reads as
 *  two full sentences (the sibling option is one), and Part A's string is left
 *  untouched. The two are near-twins; if a future round unifies them, unify
 *  DOWNWARD to one constant rather than editing both. */
export const DUPLICATE_LINKEDIN_ACTION_SENTENCES: Record<DuplicateLinkedInAction, string> = {
  // THE RECRUITER'S OWN ACTION, without naming which one (operator,
  // 2026-09-15). Two earlier versions each lost one half of that.
  //
  // "I removed the LinkedIn URL from the profile in Thrive" named a METHOD, and
  // it is only one of several: editing the other record's URL reaches the same
  // state, and `duplicate_linkedin_cleared` cannot tell them apart because it
  // counts holders. The cost was a dead end — a recruiter who fixed it the
  // other way had nothing truthful to press.
  //
  // Then "Nobody else has that URL in Thrive now" fixed that by naming the
  // OUTCOME, and lost the person: it reads as an observation the product is
  // making, not as something the recruiter did and is asking us to confirm.
  // They went and changed data in another system; the sentence should say so.
  //
  // The threshold moves to `description`, which is where it belongs — it is the
  // condition being checked, not the action being taken.
  fixedInThrive: "I made changes in Thrive. Please check again.",
  skipForNow: "Skip this person for now. They stay on the list and are not added to Thrive.",
};

/** The W1 card's option rows — the SAME two actions, a different first verb.
 *
 *  W1 is the pile-up: Thrive already holds several contacts on one URL, so the
 *  fix is a MERGE, not a URL removal. Reusing the sentence above would tell the
 *  recruiter to do the wrong thing — remove a URL and the pile-up survives,
 *  minus one address.
 *
 *  The re-check behind it is also not the same question, and the endpoint is
 *  told so: `mode=thrive_duplicates`, where a single survivor is SUCCESS. Under
 *  the default rule one survivor reads as an unresolved collision, which would
 *  shake at a recruiter who merged perfectly.
 *
 *  `skipForNow` is shared verbatim — it is the same persisted dismissal, and
 *  two wordings for one action is the collision this module exists to prevent.
 */
export const THRIVE_DUPLICATES_ACTION_SENTENCES: Record<DuplicateLinkedInAction, string> = {
  // SHARED VERBATIM with the sibling above, and that is the finding rather than
  // a shortcut. `W1_DETAIL` already says the pile-up is "either these are
  // different people and one URL is wrong, or it is one person duplicated in
  // Thrive and the records should be merged… we cannot tell which case this is.
  // Both are fixed in Thrive." The old "I merged them in Thrive" made the
  // recruiter declare one of the two possibilities that paragraph had just
  // refused to choose between — and `duplicate_linkedin_collapsed` never cared
  // either, since it counts holders.
  //
  // Once neither button names a method, the two ACTIONS are the same sentence:
  // "I went and changed data in Thrive." What differs is the CONDITION each
  // re-check applies, and that lives in `description` below, where the two are
  // genuinely different — one survivor here, none there.
  fixedInThrive: DUPLICATE_LINKEDIN_ACTION_SENTENCES.fixedInThrive,
  skipForNow: DUPLICATE_LINKEDIN_ACTION_SENTENCES.skipForNow,
};

/** The W1 section heading.
 *
 *  It stopped being "Can't be resolved here" on 2026-09-10 (operator). That
 *  title was true when the card offered nothing: the fix lives in Thrive, and
 *  the section said so. Now the card carries the two decisions the recruiter
 *  can actually take — go merge and re-check, or skip — so a heading announcing
 *  that nothing can be done here contradicts the controls beneath it. */
export const FIX_IN_THRIVE_SECTION = "Fix in Thrive first";

/** Trailing link on the `fixedInThrive` row — the fix-it-on-the-spot route.
 *
 *  It renders in the primitive's `trailing` slot, OUTSIDE the activating
 *  element, never inside the label: a label spanning the full row swallows the
 *  gaps around an interactive child, and over the link itself the browser fires
 *  BOTH. Same rule `CompanyChangeRow`'s label follows. */
export const OPEN_THRIVE_RECORD_LABEL = "Open in Thrive →";

/** Both options while the Thrive read is in flight. One read per click: no
 *  fan-out, no polling, no retry loop. */
export const RECHECK_PENDING_TITLE = "Checking Thrive…";

/** `outcome: "still_colliding"` — the failure path, and the more valuable half.
 *
 *  UNRATIFIED COPY. The mechanics are settled (shake + toast, the decision
 *  stays unmade); the exact wording is listed OPEN in the PRD and this is a
 *  serviceable placeholder, not an approved string.
 *
 *  The constraint on it: this is NOT about catching liars. Jose reframed
 *  Jared's "even if they lie to us" as ordinary human error and Jared agreed,
 *  so the sentence has to reach the recruiter who genuinely believed they had
 *  fixed it. It states what Thrive still holds and what to do next; it does not
 *  say "you said", and it does not name the recruiter as the cause. */
export const RECHECK_STILL_COLLIDING =
  "That LinkedIn URL is still on the Thrive record. Open it, remove the URL there, then check again.";

/** `outcome: "check_failed"` — neither outcome. NOT a shake.
 *
 *  A failed read is a deliberate HTTP 200 carrying `outcome: "check_failed"`,
 *  so it does not vanish into the client's generic error path. It is used when
 *  the server sends no `detail`; when it does, the server's recruiter-safe line
 *  wins. Shaking the card here would blame a recruiter for a Thrive rate
 *  limit. */
export const RECHECK_CHECK_FAILED = "We couldn't check Thrive right now. Nothing has changed.";

/** `outcome: "cleared"` — what the card says once the collision is gone. */
export const RECHECK_CLEARED_VERDICT =
  "The LinkedIn URL is free in Thrive. This row can be added as a new contact.";

/** The honest half of the cleared state, and it is not a footnote.
 *
 *  THE ENDPOINT PERSISTS NOTHING, deliberately: retiring the registry pair on a
 *  cleared verdict would contradict `stale_registry_pairs`' documented refusal
 *  to delete firm-wide state on a zero-match observation, and the pair stays
 *  semantically true (the Thrive record still is not this person). So the row
 *  re-renders from the 200 body and `get_compare` re-derives the collision from
 *  the surviving pair on the next load. Saying so is the alternative to faking
 *  durability in the client. */
export const RECHECK_CLEARED_NOTE =
  "Checked just now. This card comes back if you reload. The 'not the same person' decision is still on file, and Undo is how you retire it.";

/** The celebration's one line. On by default; `?gamification=off` opts out (Trello #790
 *  reviewed 2026-09-15; it ships on). It rewards the OUTCOME, never the
 *  click — it is fired from the response's `cleared` branch. */
export const RECHECK_CELEBRATION_LINE = "Nice. That URL is clear now.";

/** The celebration line once the contact is REALLY in the search.
 *
 *  It states a fact about Thrive, so it is only ever rendered from a
 *  `SingleContactExport` whose outcome is `added` — the synchronous add exists
 *  precisely so this sentence has something true behind it. Rendering it off a
 *  202 would be the participation trophy this epic keeps refusing. */
export const ADDED_TO_SEARCH_LINE = "Added to the search";

/** The same moment, for a row that MATCHED an existing Thrive contact.
 *
 *  "Added to the search" would be true but misleading here: nothing was
 *  created — the recruiter said "same person", and what happened is that the
 *  matched contact gained this row's details. Saying "added" of a contact the
 *  firm has had for two years reads as a duplicate, which is the one thing this
 *  whole epic is trying not to do (operator, 2026-09-11).
 *
 *  The candidacy is still written when it is missing; the word names the part
 *  that is NEW to the recruiter, which is the update. */
export const UPDATED_IN_THRIVE_LINE = "Updated in Thrive";

/** While the add is in flight. Present tense, because it is happening — and
 *  the row stays put until it is done. */
export const ADDING_TO_SEARCH_LINE = "Adding to the search…";

/** The add came back `pending`: we stopped watching, the job did not stop.
 *
 *  NOT AN ERROR, and worded so nobody reads one. The recruiter's decision is
 *  recorded and the write is queued behind somebody's large batch; the row
 *  stays where it is rather than vanishing on a promise. */
export const ADD_STILL_RUNNING_LINE =
  "Still adding. This one is behind a larger batch, and it will finish on its own.";

/** THE DONE LIST — what the recruiter achieved, kept on screen.
 *
 *  The row leaving is the right outcome and the wrong ending: it erases the
 *  work at the moment it succeeds, and a recruiter who resolves twenty
 *  conflicts finishes with an empty screen and nothing to show for it
 *  (operator, 2026-09-11). Each completed write now leaves a line here.
 *
 *  It names the CONTACT and what happened TO THEM, because that is what the
 *  recruiter remembers doing — not which internal path the write took.
 */
export const DONE_SECTION_TITLE = "Completed";

/** The big line on the burst. "Well done" and not "Nice" (operator,
 *  2026-09-11): the recruiter left this product, did work in another one, came
 *  back and had it verified. That is worth more than a nod. */
export const WELL_DONE_LINE = "Well done!";

export function doneChangedRole(name: string, company: string | null): string {
  // The company when we have it: "changed role at Globex" is a fact the
  // recruiter can check against what they just read, and a bare "changed role"
  // is not.
  return company
    ? `${name} changed role at ${company} in Thrive.`
    : `${name} changed role in Thrive.`;
}

export function doneUpdated(name: string): string {
  return `${name} was updated in Thrive.`;
}

/** The W1 merge: the recruiter did work OUTSIDE this product, and the line says
 *  so. "Cleaned and verified" is the whole point — we checked, it was true. */
export function doneCleanedAndVerified(name: string): string {
  return `${name} was updated. The duplicate Thrive contact is cleaned and verified.`;
}

export function doneAddedToSearch(name: string): string {
  return `${name} was added to Thrive and to the search.`;
}

/** The add failed. The worker's own reason follows when it sent one. */
export const ADD_FAILED_LINE = "We couldn't add this one to the search.";

/** The detail after ADD_FAILED_LINE when the server held the add back because
 *  this contact's Compare is missing or older than the window (`skipped` with
 *  `skip_reason` `not_compared` / `compare_too_old`, Trello #823). Nothing was
 *  written. */
export const ADD_NEEDS_COMPARE_DETAIL = "Run the comparison again, then add.";

/** The person panel's toast when its single add was held back for the same
 *  reason. About one person, so not the server's batch sentence. */
export function personAddNeedsCompareToast(reason: "not_compared" | "compare_too_old"): string {
  return reason === "not_compared"
    ? "Not added: this contact was never compared with Thrive. Run the comparison, then add."
    : "Not added: this contact was compared too long ago. Run the comparison again, then add.";
}

/** Under `Confirm all`, once at the end, when the pass left rows out for that
 *  reason. The pass does not stop: the others were added. These rows have no
 *  per-row status line, so without it the click would look like nothing.
 *
 *  Says neither cause: the count mixes rows never compared with rows compared
 *  too long ago (`skip_reason` either way), and one comparison clears both
 *  (/pr-review iter 6, Agents A and C). Same words as the panel's line. */
export function confirmAllNeedsCompareLine(n: number): string {
  return `${n} not added: ${n === 1 ? "needs" : "need"} a new comparison with Thrive. Run the comparison, then add ${n === 1 ? "it" : "them"}.`;
}

/** The worker DECLINED to write — a verdict, not a breakage.
 *
 *  It had no arm of its own until 2026-09-11 and fell through to the failure
 *  line, which reads as "something broke" when in fact the export looked at the
 *  row and said no. The commonest cause is a conflict the row still carries, so
 *  the wording points back at the decision rather than at the machinery. */
export const ADD_SKIPPED_LINE = "Not added. This row still has something unresolved.";

/** The modal's primary action while anything is still pending.
 *
 * A FULL SENTENCE ON A BUTTON, deliberately, and the operator's own words
 * (2026-09-14). The control is pressed at the end of a sitting to answer one
 * question — *am I done?* — and a label naming the mechanism ("Refresh") leaves
 * the recruiter to work out why they would want it. This one answers it before
 * the click. The footer CTA is wide enough to carry it.
 */
export const RERUN_COMPARE_LABEL = "Run the comparison again and see what is left";

/** Mid-run label for the same control. */
export const RERUN_COMPARE_RUNNING_LABEL = "Checking…";

/** What the add-to-search CTA says when no compare has run yet.
 *
 * REPLACES A PANEL (operator, 2026-09-15). Pressing the CTA used to open the
 * export panel, which then explained that a compare has to happen first — a
 * surface to read, understand and close in order to learn one fact. The button
 * says it in place instead, paired with the product's rejection shake.
 *
 * NAMES THE NEXT ACTION, not the rule it is enforcing. "Compare is required"
 * states a constraint; this states what to press, which is the only part the
 * recruiter can act on. */
export const COMPARE_FIRST_TOAST =
  "Run Compare to Thrive first. It checks who is already there before anything is written.";
